import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  getCustomTemplates,
  createCustomTemplate,
  deleteCustomTemplate,
} from '../../services/quoteProposalService';
import type { CustomTemplate } from '../../services/quoteProposalService';

// Template preview images
import T1Img1 from '../../assets/images/Insurance Template - 1.svg';
import T1Img2 from '../../assets/images/Insurance Template - 4.svg';
import T1Img3 from '../../assets/images/Insurance Template - 6.svg';
import T1Img4 from '../../assets/images/Insurance Template - 8.svg';
import T1Img5 from '../../assets/images/Insurance Template - 9.svg';
import T1Img6 from '../../assets/images/Insurance Template - 7.svg';
import T2Img1 from '../../assets/images/Insurance-template2-img2.png';
import T2Img2 from '../../assets/images/Insurance-template2-img3.png';
import T2Img3 from '../../assets/images/Insurance-template2-img4.png';
import T2Img4 from '../../assets/images/Insurance-template2-img5.png';
import T2Img5 from '../../assets/images/Insurance-template2-img6.png';
import T3Img1 from '../../assets/images/A4 - 3.svg';
import T3Img2 from '../../assets/images/A4 - 4.svg';

interface Props {
  selectedTemplate: number;
  selectedCustomTemplate: number;
  onTemplateSelect: (id: number) => void;
  onCustomTemplateSelect: (id: number, name: string, parentId: number) => void;
  companyLogo?: string;
  showToast: (type: 'success' | 'error' | 'warning', msg: string) => void;
}

const DEFAULT_TEMPLATES = [
  {
    id: 1,
    title: 'Modern Grid',
    description: 'Clean grid layout with emphasis on visuals and key metrics',
    images: [T1Img1, T1Img2, T1Img3, T1Img4, T1Img5, T1Img6],
    imageCount: 3,
  },
  {
    id: 2,
    title: 'Classic Compare',
    description: 'Traditional side-by-side comparison with detailed breakdowns',
    images: [T2Img1, T2Img2, T2Img3, T2Img4, T2Img5],
    imageCount: 1,
  },
  {
    id: 3,
    title: 'Modern Template',
    description: 'Elegant minimal design with a modern feel',
    images: [T3Img1, T3Img2],
    imageCount: 1,
  },
];

const TemplateSettingsSection: React.FC<Props> = ({
  selectedTemplate,
  selectedCustomTemplate,
  onTemplateSelect,
  onCustomTemplateSelect,
  companyLogo,
  showToast,
}) => {
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<{ title: string; images: string[]; idx: number } | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [createName, setCreateName] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createImageCount, setCreateImageCount] = useState(0);
  const [imageFiles, setImageFiles] = useState<(File | null)[]>([]);
  const [imagePreviews, setImagePreviews] = useState<(string | null)[]>([]);
  const [creating, setCreating] = useState(false);
  const [nameError, setNameError] = useState('');
  const [descError, setDescError] = useState('');
  const [imageError, setImageError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    loadCustomTemplates();
  }, []);

  const loadCustomTemplates = async () => {
    setLoadingTemplates(true);
    const templates = await getCustomTemplates();
    setCustomTemplates(templates);
    setLoadingTemplates(false);
  };

  const openPreview = (title: string, images: string[]) => {
    setPreviewTemplate({ title, images, idx: 0 });
  };

  useEffect(() => {
    if (!previewTemplate) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft')
        setPreviewTemplate(p => p && p.images.length > 1 ? { ...p, idx: (p.idx - 1 + p.images.length) % p.images.length } : p);
      else if (e.key === 'ArrowRight')
        setPreviewTemplate(p => p && p.images.length > 1 ? { ...p, idx: (p.idx + 1) % p.images.length } : p);
      else if (e.key === 'Escape')
        setPreviewTemplate(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [previewTemplate]);

  const openCreateForm = (templateId: number, imageCount: number) => {
    setCreateParentId(templateId);
    setCreateImageCount(imageCount);
    setCreateName('');
    setCreateDesc('');
    setNameError('');
    setDescError('');
    setImageError('');
    setImageFiles(Array(imageCount).fill(null));
    setImagePreviews(Array(imageCount).fill(null));
    fileInputRefs.current = Array(imageCount).fill(null);
    setShowCreateForm(true);
  };

  const handleImageSelect = (index: number, file: File | null) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!['jpg', 'jpeg', 'png'].includes(ext)) {
      showToast('warning', 'Only JPG and PNG images are allowed');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreviews((prev) => {
        const updated = [...prev];
        updated[index] = reader.result as string;
        return updated;
      });
    };
    reader.readAsDataURL(file);
    setImageFiles((prev) => {
      const updated = [...prev];
      updated[index] = file;
      return updated;
    });
  };

  const handleCreate = async () => {
    let hasError = false;
    setNameError('');
    setDescError('');
    setImageError('');
    if (!createName.trim()) { setNameError('Please enter a template name.'); hasError = true; }
    if (!createDesc.trim()) { setDescError('Please enter a description.'); hasError = true; }
    const uploadedCount = imageFiles.filter((f) => f !== null).length;
    if (uploadedCount !== createImageCount) { setImageError(`Please upload all ${createImageCount} images.`); hasError = true; }
    if (hasError) return;

    setCreating(true);
    const formData = new FormData();
    formData.append('Name', createName);
    formData.append('ParentTemplateId', String(createParentId || 0));
    formData.append('CoverageType', 'Auto');
    formData.append('Title', createName);
    formData.append('ProposalYear', String(new Date().getFullYear()));
    formData.append('ProposalBreakdownDescription', createDesc);
    formData.append('ProposalComparisonDescription', '');
    formData.append('AcceptanceFormDescription', '');
    imageFiles.filter((f): f is File => f !== null).forEach((f) => formData.append('Images', f));

    const ok = await createCustomTemplate(formData);
    setCreating(false);
    if (ok) {
      showToast('success', 'Custom template created successfully');
      setShowCreateForm(false);
      loadCustomTemplates();
    } else {
      showToast('error', 'Failed to create custom template');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const ok = await deleteCustomTemplate(deleteConfirm.id);
      setDeleting(false);
      if (ok) {
        showToast('success', 'Template deleted');
        setDeleteConfirm(null);
        if (selectedCustomTemplate === deleteConfirm.id) {
          onCustomTemplateSelect(0, '', 0);
        }
        loadCustomTemplates();
      } else {
        showToast('error', 'Failed to delete template');
      }
    } catch {
      setDeleting(false);
      showToast('error', 'Failed to delete template');
    }
  };

  const getCustomTemplateImages = (t: CustomTemplate): string[] => {
    if (!t.images) return [];
    if (Array.isArray(t.images)) {
      return typeof t.images[0] === 'string'
        ? (t.images[0] as string).split(',').filter((s) => s.trim())
        : (t.images as string[]);
    }
    return (t.images as string).split(',').filter((s) => s.trim());
  };

  return (
    <div className="qp-tpl-settings">
      {/* Default Templates */}
      <div className="qp-settings__section-header" style={{ marginBottom: 20 }}>
        <h3 className="qp-settings__section-title">Default Templates</h3>
        <p className="qp-settings__section-desc">Choose a base proposal template</p>
      </div>

      <div className="qp-tpl-grid">
        {DEFAULT_TEMPLATES.map((t) => {
          const isSelected = selectedTemplate === t.id && selectedCustomTemplate === 0;
          return (
            <div
              key={t.id}
              className={`qp-tpl-card${isSelected ? ' qp-tpl-card--selected' : ''}`}
              onClick={() => onTemplateSelect(t.id)}
            >
              <div className="qp-tpl-card__img-wrap">
                <img src={t.images[0]} alt={t.title} className="qp-tpl-card__img" />
                <button
                  className="qp-tpl-card__preview-btn"
                  onClick={(e) => { e.stopPropagation(); openPreview(t.title, t.images); }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  Preview
                </button>
              </div>
              <div className="qp-tpl-card__body">
                <div className="qp-tpl-card__title-row">
                  <span className="qp-tpl-card__title">{t.title}</span>
                  {isSelected && (
                    <span className="qp-tpl-card__badge">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                      Active
                    </span>
                  )}
                </div>
                <p className="qp-tpl-card__desc">{t.description}</p>
                <button
                  className="qp-tpl-card__customize-btn"
                  onClick={(e) => { e.stopPropagation(); openCreateForm(t.id, t.imageCount); }}
                >
                  Customise
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Custom Templates */}
      <div className="qp-settings__section-header" style={{ marginTop: 28, marginBottom: 16 }}>
        <h3 className="qp-settings__section-title">Custom Templates</h3>
        <p className="qp-settings__section-desc">Your personalised versions of the base templates</p>
      </div>

      {loadingTemplates ? (
        <div className="qp-tpl-loading">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
          Loading custom templates…
        </div>
      ) : customTemplates.length === 0 ? (
        <p className="qp-tpl-empty">No custom templates yet. Click "Customise" on a template above to create one.</p>
      ) : (
        <div className="qp-tpl-grid">
          {customTemplates.map((t) => {
            const images = getCustomTemplateImages(t);
            const isSelected = selectedCustomTemplate === t.id;
            return (
              <div
                key={t.id}
                className={`qp-tpl-card${isSelected ? ' qp-tpl-card--selected' : ''}`}
                onClick={() => onCustomTemplateSelect(t.id, t.title || t.name, t.parentTemplateId)}
              >
                <div className="qp-tpl-card__img-wrap">
                  {images[0] ? (
                    <img src={images[0]} alt={t.name} className="qp-tpl-card__img" />
                  ) : (
                    <div className="qp-tpl-card__img-placeholder">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                    </div>
                  )}
                  {images.length > 0 && (
                    <button
                      className="qp-tpl-card__preview-btn"
                      onClick={(e) => { e.stopPropagation(); openPreview(t.name, images); }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      Preview
                    </button>
                  )}
                </div>
                <div className="qp-tpl-card__body">
                  <div className="qp-tpl-card__title-row">
                    <span className="qp-tpl-card__title">{t.name}</span>
                    {isSelected && (
                      <span className="qp-tpl-card__badge">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                        Active
                      </span>
                    )}
                  </div>
                  <span className="qp-tpl-card__custom-badge">Custom</span>
                  <button
                    className="qp-tpl-card__delete-btn"
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ id: t.id, name: t.name }); }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Galleria */}
      {previewTemplate && createPortal(
        <div className="qp-galleria-overlay" onClick={() => setPreviewTemplate(null)}>
          <div className="qp-galleria" onClick={e => e.stopPropagation()}>
            <div className="qp-galleria__header">
              <span>{previewTemplate.title} — Preview</span>
              <button className="qp-modal__close" onClick={() => setPreviewTemplate(null)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="qp-galleria__stage">
              {previewTemplate.images.length > 1 && (
                <button
                  className="qp-galleria__nav qp-galleria__nav--prev"
                  onClick={() => setPreviewTemplate(p => p ? { ...p, idx: (p.idx - 1 + p.images.length) % p.images.length } : null)}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
              )}
              <img
                src={previewTemplate.images[previewTemplate.idx]}
                alt={`Preview ${previewTemplate.idx + 1}`}
                className="qp-galleria__main-img"
              />
              {previewTemplate.images.length > 1 && (
                <button
                  className="qp-galleria__nav qp-galleria__nav--next"
                  onClick={() => setPreviewTemplate(p => p ? { ...p, idx: (p.idx + 1) % p.images.length } : null)}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              )}
              {previewTemplate.images.length > 1 && (
                <span className="qp-galleria__counter">
                  {previewTemplate.idx + 1} / {previewTemplate.images.length}
                </span>
              )}
            </div>

            {previewTemplate.images.length > 1 && (
              <div className="qp-galleria__thumbs">
                {previewTemplate.images.map((img, i) => (
                  <button
                    key={i}
                    className={`qp-galleria__thumb${i === previewTemplate.idx ? ' qp-galleria__thumb--active' : ''}`}
                    onClick={() => setPreviewTemplate(p => p ? { ...p, idx: i } : null)}
                  >
                    <img src={img} alt={`Thumb ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Create Template Modal */}
      {showCreateForm && (
        <div className="qp-modal-overlay" onClick={() => !creating && setShowCreateForm(false)}>
          <div className="qp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="qp-modal__header">
              <span>Create Custom Template</span>
              <button className="qp-modal__close" onClick={() => !creating && setShowCreateForm(false)} disabled={creating}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="qp-modal__body">
              <div className="qp-settings__field" style={{ marginBottom: 16 }}>
                <label className="qp-settings__label">Template Name</label>
                <input
                  className={`qp-settings__input${nameError ? ' qp-settings__input--error' : ''}`}
                  type="text"
                  value={createName}
                  onChange={(e) => { setCreateName(e.target.value); setNameError(''); }}
                  placeholder="e.g. My Auto Template"
                />
                {nameError && <span className="qp-settings__field-error">{nameError}</span>}
              </div>
              <div className="qp-settings__field" style={{ marginBottom: 16 }}>
                <label className="qp-settings__label">Description</label>
                <textarea
                  className={`qp-settings__textarea${descError ? ' qp-settings__input--error' : ''}`}
                  rows={2}
                  value={createDesc}
                  onChange={(e) => { setCreateDesc(e.target.value); setDescError(''); }}
                  placeholder="Brief description of this template"
                />
                {descError && <span className="qp-settings__field-error">{descError}</span>}
              </div>
              <div className="qp-settings__field">
                <label className="qp-settings__label">Upload {createImageCount} Image{createImageCount !== 1 ? 's' : ''}</label>
                <div className="qp-tpl-image-upload-grid">
                  {Array.from({ length: createImageCount }).map((_, i) => (
                    <div key={i} className="qp-tpl-image-slot">
                      {imagePreviews[i] ? (
                        <div className="qp-tpl-image-preview">
                          <img src={imagePreviews[i]!} alt={`Image ${i + 1}`} />
                          <button className="qp-tpl-image-remove" onClick={() => {
                            setImagePreviews((p) => { const u = [...p]; u[i] = null; return u; });
                            setImageFiles((f) => { const u = [...f]; u[i] = null; return u; });
                          }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                      ) : (
                        <label className="qp-tpl-image-upload-btn">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                          <span>Image {i + 1}</span>
                          <input
                            ref={(el) => { fileInputRefs.current[i] = el; }}
                            type="file"
                            accept=".jpeg,.jpg,.png,image/jpeg,image/png"
                            style={{ display: 'none' }}
                            onChange={(e) => handleImageSelect(i, e.target.files?.[0] || null)}
                          />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
                {imageError && <span className="qp-settings__field-error">{imageError}</span>}
              </div>
            </div>
            <div className="qp-modal__footer">
              <button className="qp-settings__save-btn" onClick={handleCreate} disabled={creating}>
                {creating ? (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> Creating…</>
                ) : 'Create Template'}
              </button>
              <button className="qp-tpl-cancel-btn" onClick={() => setShowCreateForm(false)} disabled={creating}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="qp-modal-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="qp-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 360 }}>
            <div className="qp-modal__header">
              <span>Delete Template</span>
              <button className="qp-modal__close" onClick={() => !deleting && setDeleteConfirm(null)} disabled={deleting}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="qp-modal__body">
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
                Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>"{deleteConfirm.name}"</strong>? This cannot be undone.
              </p>
            </div>
            <div className="qp-modal__footer">
              <button
                className="qp-tpl-delete-confirm-btn"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button className="qp-tpl-cancel-btn" onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateSettingsSection;
